import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, ListGroup, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationsContext';
import PageTitle from '../components/PageTitle';
import { API_ENDPOINTS } from '../config/api';
import { getNotificationIcon, getNotificationIconColor, getNotificationIconBg } from '../utils/notificationIcons';
import { getNotificationTargetPath } from '../utils/notificationNavigation';

const NotificationsPage = () => {
  const { notifications, dismissNotification, clearAllNotifications, getNotificationCount } = useNotifications();
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedRecurringEvaluation, setSelectedRecurringEvaluation] = useState(null);
  const navigate = useNavigate();

  // Track component mounting and notifications changes
  useEffect(() => {
    console.log('NotificationsPage: Component mounted');
  }, []);

  useEffect(() => {
    console.log('NotificationsPage: Notifications changed:', notifications.length);
  }, [notifications]);

  // Complete evaluation now
  const completeEvaluationNow = (evaluation) => {
    navigate(`/evaluations/${evaluation.id}/complete`);
  };

  // Handle work log notification
  const handleWorkLogNotification = (data) => {
    if (!data) {
      console.error('Invalid work log notification data:', data);
      return;
    }
    
    // Backend notifications store residentId directly, frontend notifications store it as data.resident.id
    const residentId = data.residentId || (data.resident && data.resident.id);
    const month = data.month;
    const year = data.year;
    
    if (!residentId || month === undefined || year === undefined) {
      console.error('Missing required data for work log navigation:', { residentId, month, year });
      return;
    }
    
    navigate(`/director/work-log/${residentId}/${month}/${year}`);
  };

  // Handle leave request notification
  const handleLeaveRequestNotification = (data) => {
    navigate('/director/work-logs#tab=vacation');
  };

  // Get action button based on notification type
  const getActionButton = (notification) => {
    const userRole = localStorage.getItem('role');
    const explicitTarget = getNotificationTargetPath(notification, userRole);
    if (explicitTarget) {
      return (
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate(explicitTarget)}
        >
          <i className="bi bi-arrow-right-circle me-1"></i>
          Open
        </Button>
      );
    }

    switch (notification.type) {
      case 'evaluation':
        return (
          <Button 
            size="sm" 
            variant="success"
            onClick={() => completeEvaluationNow(notification.data)}
          >
            <i className="bi bi-check-circle me-1"></i>
            Complete Now
          </Button>
        );
      case 'worklog':
        return (
          <Button 
            size="sm" 
            variant="primary"
            onClick={() => handleWorkLogNotification(notification.data)}
          >
            <i className="bi bi-eye me-1"></i>
            View Details
          </Button>
        );
      case 'leaveRequest':
        return (
          <Button 
            size="sm" 
            variant="info"
            onClick={() => handleLeaveRequestNotification(notification.data)}
          >
            <i className="bi bi-calendar-check me-1"></i>
            Review Request
          </Button>
        );
      case 'schedule':
        return (
          <Button 
            size="sm" 
            variant="warning"
            onClick={() => navigate('/schedules')}
          >
            <i className="bi bi-calendar-week me-1"></i>
            View Schedule
          </Button>
        );
      case 'announcement':
        return (
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => {
              // Could navigate to announcements page or show modal
            }}
          >
            <i className="bi bi-megaphone me-1"></i>
            Read More
          </Button>
        );
      case 'reminder':
        return (
          <Button 
            size="sm" 
            variant="outline-primary"
            onClick={() => {
              // Check if this is an onboarding reminder based on the data
              if (notification.data && (notification.data.itemType === 'package' || notification.data.itemType === 'document' || notification.data.itemType === 'request')) {
                // This is an onboarding reminder
                const userRole = localStorage.getItem('role');
                if (userRole === 'resident') {
                  navigate('/onboarding#tab=my');
                } else {
                  navigate('/onboarding#tab=manage');
                }
              } else {
                // This is a regular reminder (evaluation, etc.)
                navigate('/attending/assigned');
              }
            }}
          >
            <i className="bi bi-bell me-1"></i>
            View Assignment
          </Button>
        );
      case 'approval':
        return (
          <Button 
            size="sm" 
            variant="success"
            onClick={() => {
              // Navigate to leave requests or show approval details
              navigate('/resident/vacation-requests');
            }}
          >
            <i className="bi bi-check-circle me-1"></i>
            View Details
          </Button>
        );
      case 'rejection':
        return (
          <Button 
            size="sm" 
            variant="danger"
            onClick={() => {
              // Navigate to leave requests or show rejection details
              navigate('/resident/vacation-requests');
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            View Details
          </Button>
        );
      case 'assignment':
        return (
          <Button 
            size="sm" 
            variant="primary"
            onClick={() => {
              // Check if this is a document assignment based on the data structure
              if (notification.data && (notification.data.itemType === 'document' || notification.data.itemType === 'package' || notification.data.itemType === 'request')) {
                // This is a document assignment, navigate to onboarding
                const userRole = localStorage.getItem('role');
                if (userRole === 'resident') {
                  navigate('/onboarding#tab=my');
                } else {
                  navigate('/onboarding#tab=manage');
                }
              } else {
                // This is an evaluation assignment, navigate to assigned evaluations
                navigate('/attending/assigned');
              }
            }}
          >
            <i className="bi bi-person-check me-1"></i>
            View Assignment
          </Button>
        );
      case 'completion':
        return (
          <Button 
            size="sm" 
            variant="success"
            onClick={() => {
              // Navigate to completed evaluations
              navigate('/director/evaluations?tab=completed');
            }}
          >
            <i className="bi bi-check-square me-1"></i>
            View Details
          </Button>
        );
      case 'overdue':
        return (
          <Button 
            size="sm" 
            variant="danger"
            onClick={() => {
              // Navigate to assigned evaluations
              navigate('/attending/assigned');
            }}
          >
            <i className="bi bi-exclamation-triangle me-1"></i>
            Complete Now
          </Button>
        );
      case 'eventReminder':
        return (
          <Button 
            size="sm" 
            variant="info"
            onClick={() => {
              // Navigate to calendar page
              navigate('/calendar');
            }}
          >
            <i className="bi bi-calendar me-1"></i>
            View Calendar
          </Button>
        );
      case 'system':
        return (
          <Button 
            size="sm" 
            variant="outline-secondary"
            onClick={() => {
              // Handle system notification
            }}
          >
            <i className="bi bi-x me-1"></i>
            Dismiss
          </Button>
        );
      case 'inServiceExam':
        return (
          <Button 
            size="sm" 
            variant="warning"
            onClick={() => {
              navigate('/director/in-service-exams');
            }}
          >
            <i className="bi bi-file-earmark-text me-1"></i>
            View Exam
          </Button>
        );
      case 'eventInvitation':
        return (
          <Button 
            size="sm" 
            variant="info"
            onClick={() => {
              navigate('/calendar');
            }}
          >
            <i className="bi bi-calendar-plus me-1"></i>
            View Event
          </Button>
        );
      case 'eventInvitationResponse':
        return (
          <Button 
            size="sm" 
            variant="success"
            onClick={() => {
              navigate('/calendar');
            }}
          >
            <i className="bi bi-calendar-check me-1"></i>
            View Response
          </Button>
        );
      case 'unlockRequest':
        return (
          <Button 
            size="sm" 
            variant="warning"
            onClick={() => {
              navigate('/director/work-logs');
            }}
          >
            <i className="bi bi-unlock me-1"></i>
            Review Request
          </Button>
        );
      case 'profile':
        return (
          <Button 
            size="sm" 
            variant="primary"
            onClick={() => {
              navigate('/profile');
            }}
          >
            <i className="bi bi-person me-1"></i>
            View Profile
          </Button>
        );
      case 'onboardingAssignment':
        return (
          <Button 
            size="sm" 
            variant="primary"
            onClick={() => {
              const userRole = localStorage.getItem('role');
              if (userRole === 'resident') {
                navigate('/onboarding#tab=my');
              } else {
                navigate('/onboarding#tab=manage');
              }
            }}
          >
            <i className="bi bi-folder-plus me-1"></i>
            View Assignment
          </Button>
        );
      case 'onboardingSubmission':
        return (
          <Button 
            size="sm" 
            variant="info"
            onClick={() => {
              const userRole = localStorage.getItem('role');
              if (userRole === 'director') {
                navigate('/onboarding#tab=submissions');
              } else {
                navigate('/onboarding#tab=my');
              }
            }}
          >
            <i className="bi bi-file-earmark-arrow-up me-1"></i>
            View Submission
          </Button>
        );
      case 'onboardingApproval':
        return (
          <Button 
            size="sm" 
            variant="success"
            onClick={() => {
              const userRole = localStorage.getItem('role');
              if (userRole === 'resident') {
                navigate('/onboarding#tab=completed');
              } else {
                navigate('/onboarding#tab=submissions');
              }
            }}
          >
            <i className="bi bi-check-circle me-1"></i>
            View Approval
          </Button>
        );
      case 'onboardingRejection':
        return (
          <Button 
            size="sm" 
            variant="danger"
            onClick={() => {
              const userRole = localStorage.getItem('role');
              if (userRole === 'resident') {
                navigate('/onboarding#tab=completed');
              } else {
                navigate('/onboarding#tab=submissions');
              }
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            View Rejection
          </Button>
        );
      case 'onboardingOverdue':
        return (
          <Button 
            size="sm" 
            variant="danger"
            onClick={() => {
              const userRole = localStorage.getItem('role');
              if (userRole === 'resident') {
                navigate('/onboarding#tab=my');
              } else {
                navigate('/onboarding#tab=manage');
              }
            }}
          >
            <i className="bi bi-exclamation-triangle me-1"></i>
            View Overdue
          </Button>
        );
      default:
        return null;
    }
  };

  // Handle recurring evaluation selection
  const handleRecurringEvaluationClick = (evaluation) => {
    setSelectedRecurringEvaluation(evaluation);
    setShowRecurringModal(true);
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown time';
    
    try {
      const now = new Date();
      const diffInMinutes = Math.floor((now - new Date(date)) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Unknown time';
    }
  };

  return (
    <div className="notifications-page">
      <PageTitle title="Notifications" description="View and manage your notifications" />
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="bi bi-bell me-2"></i>
            Notifications
          </h4>
          <Badge bg="primary" className="fs-6">
            {getNotificationCount()}
          </Badge>
        </Card.Header>
        <Card.Body>
          {notifications.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="bi bi-bell mb-3" style={{ fontSize: '48px' }}></i>
              <p>No notifications at this time</p>
            </div>
          ) : (
            <ListGroup variant="flush">
              {notifications.map((notification) => (
                (() => {
                  const NotificationIcon = getNotificationIcon(notification.type);
                  return (
                <ListGroup.Item key={notification.id} className="d-flex align-items-start">
                  <div className="d-flex align-items-start gap-3 w-100">
                    {/* Notification Icon */}
                    <div className={`rounded-circle d-flex align-items-center justify-content-center ${getNotificationIconBg(notification.type)}`} 
                         style={{ width: '56px', height: '56px', minWidth: '56px' }}>
                      <NotificationIcon className={getNotificationIconColor(notification.type)} size={24} />
                    </div>
                    
                    {/* Notification Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-1">{notification.title || 'No Title'}</h6>
                        <small className="text-muted">
                          {formatTimeAgo(notification.createdAt)}
                        </small>
                      </div>
                      <p className="mb-2 text-muted">{notification.body || 'No content available'}</p>
                      <div className="d-flex gap-2">
                        {getActionButton(notification)}
                                              <Button 
                        size="sm" 
                        variant="outline-secondary"
                        onClick={() => dismissNotification(notification.id)}
                      >
                        <i className="bi bi-x me-1"></i>
                        Dismiss
                      </Button>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
                  );
                })()
              ))}
            </ListGroup>
          )}
          
          {notifications.length > 0 && (
            <div className="mt-3">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={clearAllNotifications}
              >
                Clear All
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Recurring Evaluation Modal */}
      <Modal show={showRecurringModal} onHide={() => setShowRecurringModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Recurring Evaluation Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecurringEvaluation && (
            <div>
              <h5>{selectedRecurringEvaluation.title}</h5>
              <p><strong>Resident:</strong> {selectedRecurringEvaluation.resident?.firstName} {selectedRecurringEvaluation.resident?.lastName}</p>
              <p><strong>Type:</strong> {selectedRecurringEvaluation.type}</p>
              <p><strong>Recurrence:</strong> {selectedRecurringEvaluation.recurrence}</p>
              
              <h6 className="mt-3">All Instances:</h6>
              <ListGroup>
                {selectedRecurringEvaluation.recurringEvaluations?.map((evaluation, index) => (
                  <ListGroup.Item key={evaluation.id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Instance {index + 1}</strong>
                      <br />
                      <small className="text-muted">
                        Due: {new Date(evaluation.dueDate).toLocaleDateString()}
                        {evaluation.startTime && (
                          <span> | Start: {new Date(evaluation.startTime).toLocaleDateString()}</span>
                        )}
                        {evaluation.endTime && (
                          <span> | End: {new Date(evaluation.endTime).toLocaleDateString()}</span>
                        )}
                      </small>
                    </div>
                    <div>
                      <Badge bg={evaluation.status === 'completed' ? 'success' : evaluation.status === 'pending' ? 'warning' : 'secondary'}>
                        {evaluation.status}
                      </Badge>
                      {evaluation.status === 'pending' && (
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="ms-2"
                          onClick={() => {
                            setShowRecurringModal(false);
                            navigate(`/evaluations/${evaluation.id}/complete`);
                          }}
                        >
                          Complete
                        </Button>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecurringModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NotificationsPage; 